import { z } from 'zod';
import { Nullable } from 'global';
import { SelectOption } from 'global';
import { InventoryOptionMatch } from 'modules/inventoryOptions';
import { QuoteDepositType } from '@trnsact/trnsact-shared-types';
import { UseFormReturn } from 'react-hook-form/dist/types/form';

import { dealBuilderFormSchema, tradeInListingItemSchema } from './model/schema';

export enum DealBuilderStep {
  Contacts = 'contacts',
  Equipment = 'equipment',
  Pricing = 'pricing',
  FinanceMenu = 'financeMenu',
  ReviewSend = 'reviewSend',
}

export interface StepConfig {
  step: DealBuilderStep;
  label: string;
  subLabel?: string;
}

export enum DealSummaryTab {
  Deal = 'deal',
  Equipment = 'equipment',
}

export interface DealBuilderSummary {
  hardCost: number;
  feesAndTaxes: number;
  customerDeposit: number;
  tradeIn: number;
  balanceDue: number;
}

export interface CalculateDealBuilderSummaryInput {
  hardCost: number;
  feesAndTaxes: number;
  depositValue: Nullable<number>;
  depositType: Nullable<QuoteDepositType>;
  tradeIn: number;
}

export type DealBuilderSummaryItemColor = 'default' | 'secondary' | 'warning' | 'info';

export interface DealBuilderSummaryItemConfig {
  key: keyof DealBuilderSummary;
  label: string;
  color: DealBuilderSummaryItemColor;
}

export type DealBuilderFormValues = z.infer<typeof dealBuilderFormSchema>;

export interface DealBuilderCreditApplicationSource {
  id?: Nullable<string>;
}

export interface DealBuilderEquipmentSource {
  equipmentId: string;
  year: Nullable<string>;
  make: Nullable<string>;
  model: Nullable<string>;
  stockId: Nullable<string>;
  newUsed: Nullable<string>;
  mileage: Nullable<number>;
  retailPrice: Nullable<number>;
  tradeInValue: Nullable<number>;
  wholesaleCost: Nullable<number>;
  options: InventoryOptionMatch[];
  serialNumberVin: Nullable<string>;
  amountOwedOnTradeIn: Nullable<number>;
  assets: { id: string; source: string }[];
  additionalDescription: Nullable<string>;
  inventoryItem: Nullable<{ inventoryId: Nullable<string> }>;
}

// Normalized view-model used by the Contacts step
export interface DealBuilderAccount {
  name: Nullable<string>;
  address: Nullable<string>;
}

export interface DealBuilderContact {
  ownerPgId: Nullable<string>;
  accountContactId: Nullable<string>;
  fullName: string;
  title: Nullable<string>;
  email: Nullable<string>;
  phone: Nullable<string>;
}

export interface DealBuilderContactsData {
  account: Nullable<DealBuilderAccount>;
  primaryContact: Nullable<DealBuilderContact>;
  additionalRecipients: DealBuilderContact[];
  allContacts: DealBuilderContact[];
}

export interface SelectedContactsSummary {
  mainContact: Nullable<DealBuilderContact>;
  additionalCount: number;
  avatarIndex: number;
  selectedContacts: DealBuilderContact[];
}

export enum SendVia {
  Email = 'Email',
  Text = 'Text',
  TextAndEmail = 'TextAndEmail',
}

export interface SendEmailOption extends SelectOption<string> {
  fullName: string;
  email: string;
  title: string;
  group: EmailOptionGroups;
}

export enum EmailOptionGroups {
  CustomerContacts = 'Customer Contacts',
  DealershipContacts = 'Dealership Contacts',
  OtherContacts = 'Other Contacts',
}

export type TradeInListingItem = z.infer<typeof tradeInListingItemSchema>;
export type TradeInListingConfig = { form: UseFormReturn<DealBuilderFormValues> };

export interface GetTradeInListingColumnsParams {
  onFieldBlur: (rowIndex: number) => void;
  onRemoveRow: (rowIndex: number) => void;
}

export type DealSummaryCardTypes =
  | 'equipmentTotal'
  | 'deposit'
  | 'balanceDue'
  | 'estimatedPayment'
  | 'customerSavings'
  | 'grossProfit'
  | 'profitMargin';
